// src/web/InstitutionalPrograms.jsx

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';
import AVIcon from '../../components/icons/AllVoicedIcons';
import InstitutionalFAQ from '../../components/faq/InstitutionalFAQ';

export default function InstitutionalPrograms() {
  return (
    <main className="service-landing-page institutional-programs-page">
      <Helmet>
        <title>Institutional Programs | All-Voiced</title>
        <meta
          name="description"
          content="Configure structured educational environments for auditory-perceptual voice evaluation through skill development, authentic assessment, learning analytics, cohort implementation, and educational research."
        />
      </Helmet>

      <PageTracker pageName="InstitutionalPrograms" />

      <section className="service-landing-hero">
        <span className="services-eyebrow">
          Institutional Programs
        </span>

        <h1>
          Structured Educational Environments for Auditory-Perceptual Voice
          Evaluation
        </h1>

        <p>
          Skill development, authentic assessment, learning analytics,
          educational research, and course or cohort implementation for
          institutions developing auditory-perceptual voice evaluation
          expertise.
        </p>

        <div className="services-hero-cta web-cta-row">
          <a
            className="web-button web-button--primary"
            href="/resources/all-voiced-for-universities.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download brochure
          </a>

          <Link
            className="web-button web-button--secondary"
            to="/meet"
          >
            Schedule a conversation
          </Link>
        </div>

        <div className="service-landing-panel">
          <span>Skill Development</span>
          <span className="service-landing-separator">•</span>
          <span>Assessment</span>
          <span className="service-landing-separator">•</span>
          <span>Learning Analytics</span>
          <span className="service-landing-separator">•</span>
          <span>Educational Research</span>
        </div>
      </section>

      <section className="service-landing-section service-landing-intro">
        <span className="services-label">
          Why institutional programs?
        </span>

        <h2>
          From listening activities to educational infrastructure
        </h2>

        <p>
          All-Voiced helps institutions connect structured listening
          activities with assessment, learning analytics, cohort
          implementation, and educational research within a shared
          educational environment.
        </p>

        <div className="service-landing-grid four">
          <div className="service-landing-card">
            <AVIcon icon="listen" size={28} color="#209385" />

            <h3>Skill Development</h3>

            <p>
              Structured listening activities can support perceptual skill
              development through repeated practice, comparison, feedback,
              and exposure to diverse voice samples.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="assessment" size={28} color="#209385" />

            <h3>Assessment</h3>

            <p>
              Authentic listening-based activities can generate evidence of
              participation, perceptual judgment, and competency development
              where supported by the implementation model.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="analytics" size={28} color="#209385" />

            <h3>Learning Analytics</h3>

            <p>
              Participation, completion, consistency, agreement patterns,
              and progression may become visible across learners or cohorts
              when supported by the selected workflows and data model.
            </p>
          </div>

          <div className="service-landing-card">
            <AVIcon icon="graduationCap" size={28} color="#209385" />

            <h3>Educational Research</h3>

            <p>
              Structured educational environments can support teaching
              innovation, learning studies, evidence generation, and research
              on perceptual expertise development.
            </p>
          </div>
        </div>

        <div className="service-landing-statement">
          <p>
            Skill development, assessment, analytics, and educational
            research can operate within the same structured environment.
          </p>

          <p>
            What begins as a listening activity can also contribute to
            understanding how perceptual expertise develops across learners,
            courses, institutions, and time.
          </p>
        </div>
      </section>

      <section className="service-landing-section service-landing-callout">
        <span className="services-label">
          Authentic assessment
        </span>

        <h2>
          Why listening-based activities matter in the AI era
        </h2>

        <p>
          As generative AI transforms higher education, universities are
          exploring assessment models that provide credible evidence of
          genuine student engagement and human performance.
        </p>

        <p>
          Auditory-perceptual voice evaluation offers a distinctive example:
          students must listen, compare, interpret, make perceptual judgments,
          and connect those judgments to clinical reasoning.
        </p>

        <Link
          className="web-button web-button--secondary"
          to="/insights/authentic-assessment-ai-era"
        >
          Read the insight
        </Link>
      </section>

      <section className="service-landing-section service-landing-highlight">
        <div className="service-landing-two-columns">
          <div>
            <span className="services-label">
              Educational implementation
            </span>

            <h2>
              Evidence from a Graduate Voice Disorders Course
            </h2>

            <p>
              All-Voiced was integrated into a graduate-level Voice Disorders
              course at the Université de Montréal as part of a hybrid
              learning experience combining classroom instruction,
              structured calibration, and online practice.
            </p>

            <div className="service-landing-badge">
              Peer-reviewed publication · Journal of Voice (2025)
            </div>

            <ul>
              <li>Improved auditory-perceptual accuracy</li>
              <li>Closer alignment with expert reference ratings</li>
              <li>Benefits maintained over time</li>
              <li>
                Evidence supporting hybrid learning models for perceptual
                voice assessment
              </li>
            </ul>

            <p className="institutional-programs-study-link">
              <a
                href="https://doi.org/10.1016/j.jvoice.2025.11.029"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read the published study ↗
              </a>
            </p>
          </div>

          <div className="service-landing-image-card">
            <img
              src="/images/insights/2026/authentic-assessment-ai-era/Montreal.png"
              alt="Students using All-Voiced at Université de Montréal"
            />

            <p>
              Educational implementation of All-Voiced within a
              graduate-level Voice Disorders course at the Université de
              Montréal. Photo: Ingrid Verduyckt.
            </p>
          </div>
        </div>
      </section>

      <section className="service-landing-section">
        <span className="services-label">
          Implementation models
        </span>

        <h2>
          Flexible ways to start
        </h2>

        <p>
          Institutional Programs can begin with focused course activities and
          progressively evolve into configured implementations for modules,
          courses, cohorts, continuing education, or educational research.
          Analytics, reporting, competency evidence, and other capabilities
          depend on the selected activities, implementation scope, and data
          model.
        </p>

        <div className="service-landing-grid">
          <div className="service-landing-card">
            <h3>Course Integration</h3>

            <p>
              Integrate structured perceptual activities into an existing
              course.
            </p>

            <ul>
              <li>CAPE-V-related activities</li>
              <li>Structured listening exercises</li>
              <li>Independent student work</li>
              <li>Classroom practice</li>
            </ul>
          </div>

          <div className="service-landing-card">
            <h3>Custom Educational Modules</h3>

            <p>
              Create institution-specific learning environments aligned with
              course or program objectives.
            </p>

            <ul>
              <li>Custom collections</li>
              <li>Configured workflows</li>
              <li>Tailored feedback</li>
              <li>Program-specific activities</li>
            </ul>
          </div>

          <div className="service-landing-card">
            <h3>Program or Cohort Implementation</h3>

            <p>
              Support a defined group of learners across phases, cohorts, or
              program progression.
            </p>

            <ul>
              <li>Program phases</li>
              <li>Cohort workflows</li>
              <li>Progress monitoring</li>
              <li>Competency evidence where supported</li>
            </ul>
          </div>

          <div className="service-landing-card">
            <h3>Educational Research Implementation</h3>

            <p>
              Structure educational use to support learning studies, teaching
              innovation, evidence generation, or publication-oriented work.
            </p>

            <ul>
              <li>Learning studies</li>
              <li>Teaching innovation projects</li>
              <li>Structured educational data</li>
              <li>Cross-institutional collaboration</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Related service line
        </span>

        <h2>
          Institutional implementation builds on perceptual skill development
        </h2>

        <p>
          Perceptual Skill Development focuses on how individuals and groups
          engage with structured listening activities and Live Sessions.
          Institutional Programs configure these and other capabilities
          within courses, cohorts, assessment models, analytics, educational
          research, or broader institutional implementation.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>Perceptual Skill Development</h3>

            <p>
              Explore available approaches to independent practice,
              structured listening, feedback, and facilitated group learning.
            </p>
          </div>

          <Link
            className="web-button web-button--secondary"
            to="/perceptual-skill-development"
          >
            Explore skill development
          </Link>
        </div>
      </section>

      <InstitutionalFAQ />

      <section className="service-landing-section service-landing-resources">
        <span className="services-label">
          Resources
        </span>

        <h2>
          All-Voiced for Universities
        </h2>

        <p>
          The first available brochure focuses on universities and academic
          programs interested in structured auditory-perceptual voice
          evaluation, assessment, educational implementation, and research.
        </p>

        <div className="service-landing-resource-card">
          <div>
            <h3>University brochure</h3>

            <p>
              Institutional Programs for auditory-perceptual voice
              evaluation.
            </p>
          </div>

          <a
            className="web-button web-button--primary"
            href="/resources/all-voiced-for-universities.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download PDF
          </a>
        </div>

        <div className="institutional-programs-coming-soon">
          <span>Clinical teams — coming later</span>
          <span>Professional organizations — coming later</span>
        </div>
      </section>

      <section className="services-final-cta">
        <h2>
          Explore an institutional implementation
        </h2>

        <p>
          Institutional Programs may be delivered by All-Voiced,
          co-developed with partners, or configured collaboratively with
          institutions using All-Voiced as shared educational infrastructure.
        </p>

        <div className="services-hero-cta web-cta-row">
          <Link
            className="web-button web-button--primary"
            to="/meet"
          >
            Schedule a conversation
          </Link>

          <Link
            className="web-button web-button--secondary"
            to="/contact"
          >
            Get in touch
          </Link>
        </div>
      </section>

      <ScrollTracker pageName="InstitutionalPrograms" />
    </main>
  );
}
